/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import {resolveAssetUrl} from "qqq/utils/PathUtils";


/*******************************************************************************
 ** a full URL to a table's query screen, with a filter (as json) and, if given,
 ** columns (in QueryColumnsLinkFormat's compact form).  tablePath is the app-tree
 ** path from the metadata, so it gets the app's base path put in front of it - the
 ** router adds that on its own, but a link copied out of the app has to carry it.
 **
 ** The columns are left as-is rather than percent-encoded: their separators are
 ** all legal in a query string, which keeps the link both shorter and readable.
 *******************************************************************************/
export function buildViewLinkUrl(origin: string, tablePath: string, filterJson: string, columnsForLink: string): string
{
   const filterOnlyUrl = `${origin}${resolveAssetUrl(tablePath)}?filter=${encodeURIComponent(filterJson)}`;
   return (columnsForLink ? `${filterOnlyUrl}&columns=${columnsForLink}` : filterOnlyUrl);
}


/*******************************************************************************
 ** what "Copy Link to Current View" should do:
 **  - view:       copy the full view link.
 **  - filterOnly: the view link is too long, but the filter-only link is not -
 **                offer that one (columns left out) instead of copying anything.
 **  - tooLong:    even the filter-only link is too long - copy nothing.
 ** viewLength is the full view link's length, for telling the user.
 *******************************************************************************/
export type ViewLinkChoice =
   {kind: "view", url: string}
   | {kind: "filterOnly", url: string, viewLength: number}
   | {kind: "tooLong", viewLength: number};


/*******************************************************************************
 ** choose between the full view link and the filter-only link, given the
 ** longest link that may be produced.
 *******************************************************************************/
export function chooseViewLink(filterOnlyUrl: string, viewUrl: string, maxLength: number): ViewLinkChoice
{
   if (viewUrl.length <= maxLength)
   {
      return ({kind: "view", url: viewUrl});
   }

   if (filterOnlyUrl != viewUrl && filterOnlyUrl.length <= maxLength)
   {
      return ({kind: "filterOnly", url: filterOnlyUrl, viewLength: viewUrl.length});
   }

   return ({kind: "tooLong", viewLength: viewUrl.length});
}
